import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { X, Mic2, Loader2 } from "lucide-react";

export default function LyricsPanel({ onClose }) {
  const { currentTrack, current, seek, duration } = useMusicPlayer();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    if (!currentTrack?.id) return;
    setData(null);
    api.get(`/music/lyrics/${currentTrack.id}`).then((r) => setData(r.data)).catch(() => setData({ has: false, synced: [], plain: "" }));
  }, [currentTrack?.id]);

  const synced = data?.synced || [];
  let activeIdx = -1;
  for (let i = 0; i < synced.length; i++) {
    if (synced[i].time <= current + 0.15) activeIdx = i; else break;
  }

  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      activeRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [activeIdx]);

  return (
    <div data-testid="lyrics-panel" className="fixed bottom-[96px] right-4 z-50 w-96 max-w-[92vw] h-[420px] bg-[#141418] border border-white/10 rounded-xl shadow-2xl flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Mic2 className="w-4 h-4 text-[#FF2A4D]" /> {t("lyrics")}</h3>
        <button onClick={onClose} data-testid="lyrics-close" className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto px-5 py-6">
        {data === null ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : !data.has ? (
          <p className="text-zinc-500 text-sm text-center mt-10">{t("no_lyrics")}</p>
        ) : synced.length > 0 ? (
          <div className="space-y-3">
            {synced.map((l, i) => (
              <p key={i} ref={i === activeIdx ? activeRef : null}
                onClick={() => l.time && duration && seek(l.time / duration)}
                className={`text-base leading-snug cursor-pointer transition-colors ${i === activeIdx ? "text-white font-bold" : "text-zinc-500 hover:text-zinc-300"}`}>
                {l.text || "♪"}
              </p>
            ))}
          </div>
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans">{data.plain}</pre>
        )}
      </div>
    </div>
  );
}
